import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Lock, LogOut, RefreshCw, Download, Users, MessageSquare, Wrench, LifeBuoy, Loader2, AlertCircle, CreditCard, CalendarClock } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'sgt_admin_token';

const TABS = [
  { id: 'bookings', label: 'Bookings', icon: CalendarClock, collection: 'bookings' },
  { id: 'quiz', label: 'Quiz Leads', icon: MessageSquare, collection: 'quiz' },
  { id: 'strategy', label: 'Strategy Calls', icon: Users, collection: 'strategy' },
  { id: 'custom_build', label: 'Custom Builds', icon: Wrench, collection: 'custom_build' },
  { id: 'ongoing_support', label: 'Ongoing Support', icon: LifeBuoy, collection: 'ongoing_support' },
  { id: 'payments', label: 'Payments', icon: CreditCard, collection: 'payments' },
];

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || '');
  const [input, setInput] = useState('');
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('bookings');

  const loadLeads = useCallback(async (t) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/leads`, {
        headers: { 'X-Admin-Token': t },
      });
      setData(res.data);
      setAuthed(true);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 401) {
        setError('Invalid admin token.');
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
      } else {
        setError(err?.response?.data?.detail || err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) loadLeads(token);
  }, [token, loadLeads]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/admin/verify`, {}, {
        headers: { 'X-Admin-Token': input.trim() },
      });
      localStorage.setItem(TOKEN_KEY, input.trim());
      setToken(input.trim());
      setInput('');
    } catch (err) {
      setError(err?.response?.status === 401 ? 'Invalid admin token.' : (err.message || 'Login failed'));
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setAuthed(false);
    setData(null);
  };

  const exportCSV = () => {
    if (!data) return;
    const rows = data[activeTab] || [];
    if (rows.length === 0) return;
    const keys = Object.keys(rows[0]);
    const csv = [
      keys.join(','),
      ...rows.map((r) => keys.map((k) => `"${String(r[k] ?? '').replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sgt-${activeTab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---- LOGIN SCREEN ----
  if (!authed) {
    return (
      <div
        className="min-h-screen bg-[#050505] flex items-center justify-center px-6"
        data-testid="admin-login-page"
      >
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          onSubmit={handleLogin}
          className="glass p-10 rounded-2xl w-full max-w-md border border-[#00E599]/20"
        >
          <div className="w-16 h-16 rounded-full bg-[#00E599]/15 border border-[#00E599]/40 flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-[#00E599]" />
          </div>
          <h1 className="text-3xl font-headline font-bold text-white text-center mb-2">
            Admin Access
          </h1>
          <p className="text-sm text-[#A1A1AA] font-body text-center mb-8">
            Enter your admin token to view submitted leads.
          </p>

          <div className="space-y-2 mb-6">
            <Label htmlFor="token" className="text-white">Admin token</Label>
            <Input
              id="token"
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="sgt_admin_..."
              className="bg-[#0a0a0a] border-zinc-800 text-white"
              data-testid="admin-token-input"
              autoFocus
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-400 text-sm mb-4 font-body" data-testid="admin-login-error">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading || !input.trim()}
            className="w-full bg-[#00E599] hover:bg-[#00C985] text-[#050505] font-bold py-6"
            data-testid="admin-login-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Unlock Dashboard'}
          </Button>
        </motion.form>
      </div>
    );
  }

  // ---- DASHBOARD ----
  const counts = data?.counts || {};
  const activeRows = data?.[activeTab] || [];

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
      {/* Header */}
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-headline font-bold">Smart Growth Tech — Leads</h1>
          <p className="text-xs text-[#A1A1AA] font-body">
            {counts.total || 0} total submissions across all forms
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => loadLeads(token)}
            variant="outline"
            className="border-zinc-800 bg-transparent text-white hover:bg-zinc-900"
            disabled={loading}
            data-testid="admin-refresh-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-zinc-800 bg-transparent text-white hover:bg-zinc-900"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log out
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
          {TABS.map((t) => {
            const Icon = t.icon;
            const count = counts[t.collection] || 0;
            const active = activeTab === t.collection;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.collection)}
                data-testid={`admin-tab-${t.id}`}
                className={`p-4 rounded-xl border text-left transition-all ${
                  active
                    ? 'bg-[#00E599]/15 border-[#00E599]'
                    : 'bg-[#0a0a0a] border-zinc-800 hover:border-[#00E599]/40'
                }`}
              >
                <Icon className={`w-5 h-5 mb-2 ${active ? 'text-[#00E599]' : 'text-[#A1A1AA]'}`} />
                <div className="text-xs text-[#A1A1AA] font-body">{t.label}</div>
                <div className="text-2xl font-headline font-bold mt-1">{count}</div>
              </button>
            );
          })}
        </div>

        {/* Export CSV */}
        <div className="flex justify-end mb-4">
          <Button
            onClick={exportCSV}
            disabled={activeRows.length === 0}
            className="bg-[#0a0a0a] border border-zinc-800 hover:border-[#00E599]/40 text-white disabled:opacity-40"
            data-testid="admin-export-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV ({activeRows.length})
          </Button>
        </div>

        {/* Table */}
        <div className="glass rounded-xl overflow-hidden border border-white/10">
          {activeRows.length === 0 ? (
            <div className="p-12 text-center text-[#A1A1AA] font-body" data-testid="admin-empty-state">
              No {TABS.find((t) => t.collection === activeTab)?.label.toLowerCase()} yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-body" data-testid="admin-leads-table">
                <thead className="bg-[#0a0a0a] border-b border-white/10">
                  <tr>
                    {Object.keys(activeRows[0])
                      .filter((k) => k !== 'id')
                      .map((key) => (
                        <th
                          key={key}
                          className="px-4 py-3 text-left text-xs font-accent uppercase tracking-wider text-[#A1A1AA]"
                        >
                          {key}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {activeRows.map((row, idx) => (
                    <tr
                      key={row.id || idx}
                      className="border-b border-white/5 hover:bg-white/5 transition-colors"
                      data-testid={`admin-lead-row-${idx}`}
                    >
                      {Object.keys(row)
                        .filter((k) => k !== 'id')
                        .map((key) => {
                          const val = row[key];
                          const display =
                            val && typeof val === 'string' && val.length > 60
                              ? val.slice(0, 60) + '…'
                              : String(val ?? '');
                          return (
                            <td key={key} className="px-4 py-3 text-white whitespace-nowrap">
                              {display}
                            </td>
                          );
                        })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;
